import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError, toApiError } from '../../lib/api-error';
import type { CodeFlowRevisionSourceResponse, CodeFlowRunStepsResponse } from './types';

/**
 * The code-flow view of a run, or null when the run has none (the route answers
 * WORKFLOW_RUN_NOT_FOUND, the expected 4xx on every old-walker run). Any other failure throws.
 * Only asks when the org has the code-flows flag.
 */
export const useCodeFlowRunSteps = (runId: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['code-flow-run-steps', runId],
    enabled: user?.features?.codeFlows === true,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const status = query.state.data?.run.status;
      return status === 'queued' || status === 'running' ? 3000 : false;
    },
    queryFn: async (): Promise<CodeFlowRunStepsResponse | null> => {
      const response = await fetch(`${API_URL}/code-flows/runs/${runId}/steps`, { credentials: 'include' });
      if (response.ok) return response.json();
      const error = await toApiError(response, 'Failed to load code-flow run steps');
      if (error instanceof ApiError && error.code === 'WORKFLOW_RUN_NOT_FOUND') return null;
      throw error;
    },
  });
};

export const useCodeFlowRevisionSource = (workflowId: string, rev: number) =>
  useQuery({
    queryKey: ['code-flow-source', workflowId, rev],
    staleTime: Infinity, // revisions are immutable
    queryFn: async (): Promise<CodeFlowRevisionSourceResponse> => {
      const response = await fetch(`${API_URL}/code-flows/${workflowId}/revisions/${rev}/source`, {
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to load revision source');
      return response.json();
    },
  });
